import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dropdown,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Segment,
  Table,
} from "semantic-ui-react";
import { formatDate } from "../formatters";

const HEALTH_META = {
  disabled: { color: "grey", text: "Disabled" },
  stale: { color: "orange", text: "Stale" },
  stuck: { color: "red", text: "Stuck" },
  degraded: { color: "yellow", text: "Degraded" },
  running: { color: "blue", text: "Running" },
  healthy: { color: "green", text: "Healthy" },
  unknown: { color: "grey", text: "Unknown" },
};

const HEALTH_OPTIONS = Object.entries(HEALTH_META).map(([value, meta]) => ({
  key: value,
  value,
  text: meta.text,
}));

const TABLE_COLUMN_COUNT = 8;

function matchesSearch(item, search) {
  if (!search) return true;
  const automation = item.automation || {};
  return [
    automation.name,
    automation.slug,
    automation.owner,
    automation.environment,
    automation.automationType,
    item.latestRun?.runId,
    item.latestRun?.latestMessage,
  ].filter(Boolean).some((value) => value.toLowerCase().includes(search));
}

const AutomationOverview = ({ items, loading, error, onRefresh, onOpenAutomation, onOpenRun }) => {
  const [search, setSearch] = useState("");
  const [health, setHealth] = useState("");
  const [environment, setEnvironment] = useState("");

  const environmentOptions = useMemo(() => Array.from(new Set(items
    .map((item) => item.automation?.environment)
    .filter(Boolean)))
    .sort()
    .map((value) => ({ key: value, value, text: value })), [items]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return items.filter((item) => matchesSearch(item, normalizedSearch)
      && (!health || item.health === health)
      && (!environment || item.automation?.environment === environment));
  }, [environment, health, items, search]);

  const filtersActive = Boolean(search || health || environment);

  const renderBody = () => {
    if (loading && items.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">Loading automation health...</Table.Cell>
        </Table.Row>
      );
    }

    if (error && items.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">Automation health could not be loaded.</Table.Cell>
        </Table.Row>
      );
    }

    if (items.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">No automations are registered.</Table.Cell>
        </Table.Row>
      );
    }

    if (filteredItems.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={TABLE_COLUMN_COUNT} textAlign="center">No automations match these filters.</Table.Cell>
        </Table.Row>
      );
    }

    return filteredItems.map((item) => {
      const healthMeta = HEALTH_META[item.health] || HEALTH_META.unknown;
      const outcomes = item.outcomesLastSevenDays || {};
      return (
        <Table.Row
          key={item.automation.id}
          className="automation-monitoring__clickable-row"
          role="button"
          aria-label={`Open ${item.automation.name}`}
          onClick={() => onOpenAutomation(item.automation.id)}
          onKeyDown={(event) => {
            if (["Enter", " "].includes(event.key)) {
              event.preventDefault();
              onOpenAutomation(item.automation.id);
            }
          }}
          tabIndex={0}
        >
          <Table.Cell>
            <strong>{item.automation.name}</strong>
            <span className="automation-monitoring__secondary">{item.automation.slug}</span>
          </Table.Cell>
          <Table.Cell>{item.automation.environment}</Table.Cell>
          <Table.Cell>
            <Label color={healthMeta.color}>{healthMeta.text}</Label>
            {item.activeRunCount > 0 ? (
              <span className="automation-monitoring__secondary">{item.activeRunCount} active</span>
            ) : null}
          </Table.Cell>
          <Table.Cell>
            {item.latestRun ? (
              <>
                <strong>{item.latestRun.runId}</strong>
                <span className="automation-monitoring__secondary">
                  {item.latestRun.state} · {formatDate(item.latestRun.startedAt)}
                </span>
                <Button
                  basic
                  size="mini"
                  className="automation-monitoring__latest-run-button"
                  onKeyDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenRun(item.automation.id, item.latestRun.runId);
                  }}
                >
                  Open latest run
                </Button>
              </>
            ) : "-"}
          </Table.Cell>
          <Table.Cell>{formatDate(item.lastSuccess?.endedAt)}</Table.Cell>
          <Table.Cell>{formatDate(item.lastFailure?.endedAt)}</Table.Cell>
          <Table.Cell>
            <Label basic color="green">{outcomes.succeeded || 0} passed</Label>
            <Label basic color="red">{outcomes.failed || 0} failed</Label>
            <Label basic>{outcomes.cancelled || 0} cancelled</Label>
          </Table.Cell>
          <Table.Cell>
            {item.activeIncidents?.length
              ? item.activeIncidents.map((incident) => (
                <Label key={incident.id} color="red" basic>
                  {incident.type.replace(/_/g, " ")}
                </Label>
              ))
              : "-"}
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  return (
    <div className="automation-monitoring">
      <Segment className="automation-monitoring__header" inverted>
        <div>
          <Header as="h1">Automation Health</Header>
          <span>Workflow runs and tracer signals</span>
        </div>
        <Button color="teal" loading={loading} onClick={onRefresh}>
          <Icon name="refresh" />
          Refresh
        </Button>
      </Segment>

      {error ? <Message negative content={error} /> : null}

      <Segment className="automation-monitoring__filters">
        <Input
          icon="search"
          placeholder="Search name, owner, run, or message"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Dropdown
          clearable
          selection
          options={HEALTH_OPTIONS}
          placeholder="All health states"
          value={health}
          onChange={(event, data) => setHealth(data.value || "")}
        />
        <Dropdown
          clearable
          selection
          options={environmentOptions}
          placeholder="All environments"
          value={environment}
          onChange={(event, data) => setEnvironment(data.value || "")}
        />
        {filtersActive ? (
          <Button basic onClick={() => {
            setSearch("");
            setHealth("");
            setEnvironment("");
          }}>
            Clear filters
          </Button>
        ) : null}
      </Segment>

      <Table celled striped selectable className="automation-monitoring__overview-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Automation</Table.HeaderCell>
            <Table.HeaderCell>Environment</Table.HeaderCell>
            <Table.HeaderCell>Health</Table.HeaderCell>
            <Table.HeaderCell>Latest run</Table.HeaderCell>
            <Table.HeaderCell>Last success</Table.HeaderCell>
            <Table.HeaderCell>Last failure</Table.HeaderCell>
            <Table.HeaderCell>Last 7 days</Table.HeaderCell>
            <Table.HeaderCell>Active incidents</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderBody()}</Table.Body>
      </Table>
    </div>
  );
};

AutomationOverview.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  onRefresh: PropTypes.func.isRequired,
  onOpenAutomation: PropTypes.func.isRequired,
  onOpenRun: PropTypes.func.isRequired,
};

export default AutomationOverview;
